import Amplify from '@aws-amplify/core';
import { DASHBOARD_URL } from '@shared/utils/constants/urls';
import { EnvironmentType } from '../enums';
import { Environment } from '../interfaces';

const VISIBLE_VARIABLE_LENGTH = 13;

const ENVIRONMENT_STRING_KEYS_MAP: Map<keyof Environment, string> = new Map([
  ['awsExports', 'AWS_EXPORTS'],
  ['backendUrlDev', 'BACKEND_URL_DEV'],
  ['dashboardUrl', 'DASHBOARD_URL'],
  ['graphqlApiKey', 'GRAPHQL_API_KEY'],
  ['graphqlApiUrl', 'GRAPHQL_ENDPOINT'],
  ['mixpanelToken', 'MIXPANEL_TOKEN'],
  ['npmPackageVersion', 'NPM_PACKAGE_VERSION'],
  ['sentryAuthToken', 'SENTRY_AUTH_TOKEN'],
  ['studyReelUrl', 'STUDYREEL_APP_URL'],
  ['type', 'ENVIRONMENT'],
  ['userEmail', 'USER_EMAIL'],
  ['worksmartApiUrl', 'WORKSMART_API_URL'],
  ['sentryLogLevel', 'SENTRY_LOG_LEVEL'],
]);
const ENVIRONMENT_BOOLEAN_KEYS_MAP: Map<keyof Environment, string> = new Map([
  ['enableAnalytics', 'ENABLE_ANALYTICS'],
  ['enableSentry', 'ENABLE_SENTRY'],
]);

export function setEnvironmentVariables(
  envData: { [key: string]: string },
  environment = {} as Environment,
): Environment {
  environment = { ...environment };
  if (envData && typeof envData === 'object') {
    ENVIRONMENT_STRING_KEYS_MAP.forEach((envDataKey: string, envKey: keyof Environment) => {
      assignEnvironmentVariable(environment, envKey, envData[envDataKey]);
    });
    ENVIRONMENT_BOOLEAN_KEYS_MAP.forEach((envDataKey: string, envKey: keyof Environment) => {
      assignEnvironmentVariable(environment, envKey, envData[envDataKey] === 'true');
    });
    if (!environment.dashboardUrl) {
      environment.dashboardUrl = DASHBOARD_URL;
    }
    if (!environment.type) {
      environment.type = EnvironmentType.Development;
    }
    if (environment.awsExports) {
      environment.awsExports = JSON.parse(environment.awsExports as string);
      Amplify.configure(environment.awsExports);
    }
  }
  return environment;
}

function assignEnvironmentVariable<T extends keyof Environment>(
  environment: Environment,
  key: T,
  value: Environment[T]
): void {
  environment[key] = value;
}

export function logEnvironmentVariables(environment: Environment): void {
  if (environment.type !== EnvironmentType.Production) {
    console.log(
      'Environment variables:',
      {
        ...environment,
        backendUrlDev: hideVariableValue(environment.backendUrlDev),
        graphqlApiKey: hideVariableValue(environment.graphqlApiKey),
        graphqlApiUrl: hideVariableValue(environment.graphqlApiUrl),
        mixpanelToken: hideVariableValue(environment.mixpanelToken),
        sentryAuthToken: hideVariableValue(environment.sentryAuthToken),
      }
    );
  }
}

export function initializeEnvironment(): Environment {
  const environment: Environment = setEnvironmentVariables(process.env);
  logEnvironmentVariables(environment);
  return environment;
}

export function initializeAngularEnvironment(environment = {} as Environment): Environment {
  environment = { ...environment };
  const extensionElement = document.getElementsByTagName('da-extension')[0];
  if (extensionElement) {
    const env: string = extensionElement.getAttribute('env');
    if (env) {
      const envData = JSON.parse(env);
      environment = setEnvironmentVariables(envData, environment);
      extensionElement.removeAttribute('env');
    }
  }
  if (environment.type !== EnvironmentType.Production) {
    logEnvironmentVariables(environment);
  }
  return environment;
}

export function getAssetsPath(): string {
  const extensionElement = document.getElementsByTagName('da-extension')[0];
  if (extensionElement) {
    const assets: string = extensionElement.getAttribute('assets');
    if (assets) {
      return assets;
    }
  }
  return 'assets';
}

function hideVariableValue(value: string | null | undefined): string | null | undefined {
  if (!value) {
    return value
  };
  if (value.length <= VISIBLE_VARIABLE_LENGTH) {
    return '*'.repeat(value.length)
  };
  return value.slice(0, VISIBLE_VARIABLE_LENGTH) + '*'.repeat(VISIBLE_VARIABLE_LENGTH);
}
